import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import { TASK_FORM_ERROR_MESSAGES } from '../errors/errorMessages';
import type { FormBuilderState, CreateTaskFormResponse } from '../types';
import taskFormEndpoints from './taskFormEndpoints';
import { buildPayload } from './createTaskFormApiService';

export interface UpdateTaskFormResult {
  success: boolean;
  data?: CreateTaskFormResponse;
  error?: string;
}

function extractErrorMessage(errorData: unknown, status?: number): string {
  if (status === 401 || status === 403) {
    return TASK_FORM_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return TASK_FORM_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    if (errorData && typeof errorData === 'object' && 'message' in errorData) {
      return (errorData as { message: string }).message;
    }
    return TASK_FORM_ERROR_MESSAGES.VALIDATION_ERROR;
  }
  if (errorData && typeof errorData === 'object' && 'message' in errorData) {
    return (errorData as { message: string }).message;
  }
  return TASK_FORM_ERROR_MESSAGES.DEFAULT_ERROR;
}

export async function updateTaskForm(
  id: string,
  state: FormBuilderState
): Promise<UpdateTaskFormResult> {
  try {
    const payload = buildPayload(state);
    const url = `${taskFormEndpoints.FORMS}/${id}`;
    const result = await apiService.putAPI(url, payload);

    if (!result.error) {
      return { success: true, data: result.data as CreateTaskFormResponse };
    }

    if (result.isNetworkError) {
      return { success: false, error: TASK_FORM_ERROR_MESSAGES.NETWORK_ERROR };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: TASK_FORM_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : TASK_FORM_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
}
